import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AppLifecycleContext from "../../../lib/contexts/AppLifecycleContext";
import { IActionPayload, IColumn } from "../../components/Common/VirtualDataTable/VirtualDataTable";
import { ActionType, SortDirection } from "ka-table/enums";
import { remToPx } from "../../../lib/utils/ClassUtility";
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Paper,
  Popover,
  Text,
  TextInput,
  useMantineColorScheme,
  useMantineTheme
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import MultiSelectDropdown from "../../components/Common/Dropdown/MultiSelectDropdown";
import { numberToCommas } from "../../../lib/utils/StringUtility";
import { useDebouncedState, useLocalStorage } from "@mantine/hooks";
import { VirtualDataTable } from "../../components/Common/VirtualDataTable";
import { NoResults } from "../../components/App/NoResults/NoResults";
import { CompaniesService } from "../../../lib/services/CompaniesService";
import { ExportButton } from "../../components/Common/ExportButton/ExportButton";
import { downloadCSVFromResponse } from "../../../lib/utils/CSVExporterUtility";
import { DATE_FORMAT, WORKSPACE_MEMBER_ROLES } from "../../../lib/utils/Constants";
import { format } from "date-fns";
import {
  HeaderNavigationContainer,
  HeaderNavigationH1
} from "@/ui/components/App/HeaderNavigation/HeaderNavigation.style";
import { LS_COMPANIES_FIELDS_PREFERENCE } from "../../../lib/utils/Storage";
import { arrayMove } from "../../../lib/utils/ArrayUtility";
import useWorkspaceUtilityHook from "../../../hooks/useWorkspaceUtilityHook";
import { useSegmentStore } from "@/stores/useSegmentStore";
import { useSegmentFilterStore } from "@/stores/useSegmentFilterStore";
import { useSegmentListStore } from "@/stores/useSegmentListStore";
import { SegmentModal } from "@/ui/components/App/Segments/SegmentModal";
import { SegmentDropdownMenu } from "@/ui/components/App/Dropdowns/SegmentDropdownMenu";
import { SegmentsQueryBuilder } from "@/ui/components/App/Segments/SegmentsQueryBuilder";
import PageHeader from "@/ui/pages/ContactsHub/components/PageHeader/PageHeader";
import { useCustomizeMenuStore } from "@/stores/useCustomizeMenuStore";
import { useUserOverviewModalStore } from "@/stores/userOverviewModalStore";

const Companies = () => {
  const { colorScheme } = useMantineColorScheme();

  // Get segment from the params.
  let { segment } = useParams<{
    segment: string;
  }>();
  /**
   * Segment filter store
   */
  const [filterVisible, setFilterVisible] = useSegmentFilterStore((state) => [
    state.filterVisible,
    state.setFilterVisible
  ]);
  /**
   * Segments Store
   */
  const [segments, fetchSegments, setListsSegmentType, updateSegment] = useSegmentListStore(
    (state) => [state.segments, state.fetchSegments, state.setSegmentType, state.updateSegment]
  );

  /**
   * Segment Query builder store
   */
  const [
    filters,
    setFilters,
    resetExceptFilters,
    reset,
    modalOpen,
    setSegmentModalOpen,
    setSegmentType
  ] = useSegmentStore((state) => [
    state.filters,
    state.setFilters,
    state.resetExceptFilters,
    state.reset,
    state.modalOpen,
    state.setModalOpen,
    state.setType
  ]);

  // Get user role in the workspace.
  const { hasRolesNotWith } = useWorkspaceUtilityHook();
  const [setOverviewModalOpen] = useUserOverviewModalStore((state) => [state.setIsOpen]);
  // Navigate from react-router-dom.
  const navigate = useNavigate();
  const location = useLocation();

  // Service calls
  const companiesService = new CompaniesService();

  // For active workspace id of the user.
  const { activeWorkspace } = useContext(AppLifecycleContext);

  // Companies component state.
  const [noResults, setNoResults] = useState(false);
  const [tableFields, setTableFields, removeValue] = useLocalStorage<IColumn[]>({
    key: `${LS_COMPANIES_FIELDS_PREFERENCE}_${activeWorkspace?.identifier}`,
    defaultValue: [],
    getInitialValueInEffect: false
  });
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useDebouncedState("", 500);

  // exporting state for export companies list feature
  const [exporting, setIsExporting] = useState("idle");

  // Ref for table.
  const tableRef = useRef<any>(null);

  // To keep track of first field api call.
  const firstFieldApiCall = useRef(false);

  // To keep track of first render.
  const firstRender = useRef(false);

  const [internalAside, setInternalAside] = useCustomizeMenuStore((state: any) => [
    state.internalAside,
    state.setInternalAside
  ]);

  useEffect(() => {
    document.title = "Companies | Usermaven";
    setSegmentType("company");
    setListsSegmentType("company");
  }, []);

  // on search change
  useEffect(() => {
    if (!firstRender.current) {
      firstRender.current = true;
      return;
    }
    // if current route contains 'from_path' query param, then don't refresh the table
    if (location.search.includes("from_path")) {
      return;
    }

    if (modalOpen) {
      return;
    }
    tableRef.current?.refresh();
  }, [search, filters]);

  /**
   * get visible fields from the nested columns (object) and return an array of string with the field keys
   */
  const getVisibleFields = useCallback((columns: IColumn[]) => {
    const staticFields = ["company_id", "avatar"];
    const visibleFields = columns.filter((field) => field.visible).map((field) => field.key);

    const fields = [...staticFields, ...visibleFields];

    // remove duplicates and fields that are not needed
    return fields.filter((field, index) => fields.indexOf(field) === index);
  }, []);

  /**
   * This function is responsible for fetching the next page of data from the server.
   */
  const fetchNextPageCallback = async (
    page: number,
    sort: { key: string; direction: SortDirection }
  ) => {
    const fields = await getCompaniesFields(false);

    const sortDirection = sort.direction === SortDirection.Ascend ? "asc" : "desc";
    const sortKey = sort.key ? sort.key : "last_visited_time";

    // If the filter and the group rules are empty, then don't send the filter rules.
    // In case of the page type, the field is going to be empty.
    const filterRules =
      (filters.rules.length > 0 && !filters.rules[0].field && filters.rules[0].type !== "page") ||
      (filters.groups.length > 0 &&
        !filters.groups[0].rules[0].field &&
        filters.groups[0].rules[0].type !== "page")
        ? {}
        : filters;

    const response = await companiesService.search(activeWorkspace?.id, {
      fields: getVisibleFields(fields),
      page,
      sort: `${sortKey}:${sortDirection}`,
      search,
      // segment filters
      filters: filterVisible || segment !== "everyone" ? filterRules : {}
    });

    if (response?.data?.accounts) {
      setTotal(response?.data?.accounts_count);
      setNoResults(false);

      return {
        list: response.data.accounts,
        count: response.data.accounts_count
      };
    }

    return {
      list: [],
      count: 0
    };
  };

  // Service call to export companies list as CSV.
  const exportCompaniesList = async () => {
    setIsExporting("loading");
    // get companies fields
    const fields = await getCompaniesFields(false);

    // service call for export with visible fields parameter which export only visible fields in the CSV
    try {
      const response = await companiesService.companiesListExport(activeWorkspace.id, {
        fields: getVisibleFields(fields),
        q: search
      });
      if (response.data) {
        //Setting filename of the exported file in accordance with the date and time of export.
        let filename = `companies_list_${format(new Date(), DATE_FORMAT)}.csv`;
        //Utility function to download the csv from response.
        downloadCSVFromResponse(response, filename);
        setIsExporting("loaded");
        return true;
      }
    } catch (err) {
      setIsExporting("loaded");
      throw new Error("400");
    }
    return false;
  };

  /**
   * service call to get companies fields
   */
  const getCompaniesFields: (forcedUpdate: boolean) => Promise<IColumn[]> = async (
    forcedUpdate = false
  ) => {
    if (tableFields.length && firstFieldApiCall.current) {
      return tableFields;
    }

    const response = await companiesService.fields(activeWorkspace?.id);

    if (response?.data?.fields) {
      firstFieldApiCall.current = true;

      if (forcedUpdate) {
        setTableFields(response.data.fields);
        return response.data.fields;
      }

      const serverFields = response.data.fields
        .map((field: IColumn) => field.key)
        .sort()
        .join(",");
      const localFields = tableFields
        .map((field: IColumn) => field.key)
        .sort()
        .join(",");

      if (serverFields !== localFields) {
        setTableFields(response.data.fields);
        return response.data.fields;
      }

      return tableFields;
    }
    return [];
  };

  /**
   * This function is responsible for handling the change in the table fields.
   * @param selectedColumnsList
   */
  const onColumnVisibilityChange = (selectedColumnsList: string[]) => {
    let newFields = tableFields.map((field) => {
      field.visible = selectedColumnsList.includes(field.key);
      return field;
    });

    // Hard refresh the table.
    tableRef.current?.refresh();

    setTableFields(newFields);
  };

  /**
   * This function is responsible for handling local storage changes like column resize, column re-ordering, etc.
   * @param payload
   */
  const onColumnChange = (payload: IActionPayload) => {
    const { type, columnKey, width, targetColumnKey } = payload;

    if (type === ActionType.ResizeColumn) {
      const newFields = tableFields.map((field) => {
        if (field.key === columnKey) {
          field.width = width;
        }
        return field;
      });

      setTableFields(newFields);
    }

    if (type === ActionType.ReorderColumns) {
      const columnIndex = tableFields.findIndex((field) => field.key === columnKey);
      const targetColumnIndex = tableFields.findIndex((field) => field.key === targetColumnKey);

      // shift the column to the target column index.
      const newFields = arrayMove(tableFields, columnIndex, targetColumnIndex);

      setTableFields(newFields);
    }
  };

  /**
   * Method to reset the columns to default.
   */
  const resetColumns = async () => {
    // Remove the local storage
    removeValue();

    // Refetch the fields
    firstFieldApiCall.current = false;
    await getCompaniesFields(true);

    // Hard refresh the table.
    tableRef.current?.refresh();
  };

  /**
   * Memorized method to calculate the table height based on the sibling elements.
   */
  const tableHeight = useMemo(() => {
    // Breakdown: 50px Visitor header height
    const headerHeight = 60;

    const subHeaderHeight = 36 + remToPx(1.25 + 1.25 + 1); // Removed companies text under search // + 32

    return window.innerHeight - headerHeight - subHeaderHeight;
  }, []);

  const onNoResults = () => {
    setNoResults(true);
  };

  const onRowClick = (row: any) => {
    // Current Path
    // const currentPath = location.pathname;
    //
    // const redirectPath = `/env/${activeWorkspace.identifier}/companies/everyone/${row.company_id}?from_path=${currentPath}`;
    //
    // navigate(redirectPath);

    setOverviewModalOpen(true, "company", row.company_id);
  };

  /**
   * Method to apply the filters
   */

  const handleApplyFilters = (values: any) => {
    setFilters(values);
  };

  /**
   * Get current segment from the segments list
   */
  const currentSegmentName = useMemo(() => {
    if (segment === "everyone") {
      return "";
    }
    const s = segments.find((s: any) => s.id === segment);
    if (s) {
      return s.name;
    }

    return "";
  }, [segments, segment]);

  const [openedSegmentPopover, setOpenedSegmentPopover] = useState(false);

  /**
   * Update or create a segment
   * @param values
   */
  const handleUpdateSegment = async (values: any) => {
    const currentSegment = segments.find((s: any) => s.id === segment);

    if (currentSegment && segment !== "everyone") {
      await updateSegment(
        activeWorkspace.id,
        currentSegment.id,
        {
          name: currentSegment.name,
          type: currentSegment.type,
          filters: values,
          description: currentSegment.description
        },
        true
      );
    }
  };

  return (
    <>
      <SegmentModal />
      <Paper>
        <PageHeader>
          <Flex align={"center"} gap={"xs"} flex={1}>
            <ActionIcon
              color={"dark.2"}
              variant={"transparent"}
              onClick={() => setInternalAside(!internalAside)}>
              <FontAwesomeIcon icon={solid("bars-sort")} />
            </ActionIcon>

            <Text lineClamp={1}>
              {currentSegmentName ? <>{currentSegmentName}</> : <>Companies</>}{" "}
            </Text>
            <Text fz="xs" fw={600}>
              {" "}
              {total > 0 && <>({numberToCommas(total)})</>}
            </Text>
          </Flex>

          {/* Export Button for exporting users list as csv */}
          <Flex>
            {hasRolesNotWith([WORKSPACE_MEMBER_ROLES.VIEWER]) && (
              <ExportButton
                onClick={exportCompaniesList}
                disabled={exporting === "loading"}
                loading={exporting === "loading"}
              />
            )}
          </Flex>
        </PageHeader>

        <div className="um-companies">
          <div>
            <Flex
              align={"center"}
              justify={"space-between"}
              wrap={"wrap"}
              gap={16}
              style={{
                padding: "24px 19px"
              }}>
              <Popover
                closeOnClickOutside={false}
                opened={openedSegmentPopover}
                onChange={(opened) => setOpenedSegmentPopover(opened)}
                width={"100%"}
                position="bottom-start"
                shadow="md"
                withinPortal
                styles={{
                  dropdown: {
                    maxWidth: "990px"
                  }
                }}>
                <Popover.Target>
                  <Button
                    onClick={() => setOpenedSegmentPopover((o) => !o)}
                    size="xs"
                    variant="outline"
                    mr={"sm"}
                    color={
                      filterVisible ? (colorScheme === "dark" ? "dark.3" : "brand.5") : "gray.6"
                    }
                    leftSection={
                      filterVisible ? undefined : <FontAwesomeIcon icon={regular("plus")} />
                    }>
                    {filterVisible
                      ? `Filters (${filters.rules.length || filters.groups.length})`
                      : "Add a filter"}
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <SegmentsQueryBuilder
                    fullWidth={true}
                    withClearButton={true}
                    handleSegmentCreate={() => {
                      resetExceptFilters();
                      setSegmentModalOpen(true);
                      setOpenedSegmentPopover(false);
                    }}
                    allowSegmentCreate={true}
                    handleSubmitButtonText={currentSegmentName ? "Update" : "Apply"}
                    handleSaveButtonText={
                      currentSegmentName ? "Save as a new Segment" : "Save as a Segment"
                    }
                    handleSubmit={(filters) => {
                      setFilterVisible(true);
                      handleApplyFilters(filters);

                      handleUpdateSegment(filters);
                      setOpenedSegmentPopover(false);
                    }}
                    forcedSegmentType={"companies"}
                  />
                </Popover.Dropdown>
              </Popover>

              <Flex align={"center"} gap={16}>
                {/*<Button*/}
                {/*  size="xs"*/}
                {/*  variant="outline"*/}
                {/*  color={colorScheme === "dark" ? "dark.3" : "gray.6"}*/}
                {/*  leftIcon={*/}
                {/*    <FontAwesomeIcon icon={filterVisible ? regular("times") : regular("plus")} />*/}
                {/*  }*/}
                {/*  className="mr-4"*/}
                {/*  onClick={() => {*/}
                {/*    if (filterVisible) {*/}
                {/*      reset();*/}
                {/*    }*/}
                {/*    setFilterVisible(!filterVisible);*/}
                {/*  }}>*/}
                {/*  {filterVisible ? "Clear filters" : "Add a filter"}*/}
                {/*</Button>*/}

                <Flex align={"center"}>
                  <TextInput
                    size="xs"
                    placeholder="Search by company"
                    leftSection={<FontAwesomeIcon icon={regular("search")} />}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    defaultValue={search}
                  />
                </Flex>

                {/*<Box className="mr-4">*/}
                {/*  <SegmentDropdownMenu*/}
                {/*    type="company"*/}
                {/*    defaultValue={segment && segment !== "everyone" ? segment : undefined}*/}
                {/*    onChange={(value: any) => {*/}
                {/*      if (!value) {*/}
                {/*        reset();*/}
                {/*        navigate(`/env/${activeWorkspace.identifier}/companies/everyone`);*/}
                {/*        return;*/}
                {/*      }*/}
                {/*      setFilters(segments.find((s: any) => s.id === value).filters);*/}
                {/*      setFilterVisible(false);*/}
                {/*      navigate(`/env/${activeWorkspace.identifier}/companies/${value}`);*/}
                {/*    }}*/}
                {/*  />*/}
                {/*</Box>*/}

                <MultiSelectDropdown
                  disabled={total === 0}
                  buttonText={"Edit Displayed Attributes"}
                  onChange={onColumnVisibilityChange}
                  headerText={"Edit Columns"}
                  searchPlaceholder={"Search for a column"}
                  options={[...tableFields]}
                  initialSelectedOptions={tableFields
                    .filter((field) => field.visible)
                    .map((field) => field.key)}
                  onReset={() => resetColumns()}
                  withHeader
                  withSearch
                  withReset
                />
              </Flex>
            </Flex>

            {/* <Box
              className="flex py-2 font-bold"
              sx={{
                height: "32px",
              }}
            >
              {total > 0 && <p>{numberToCommas(total)} companies</p>}
            </Box> */}
          </div>

          {noResults && (
            <>
              <NoResults
                type="search"
                heading="No Results"
                text={search ? `No results found for "${search}".` : "No results found."}
              />
            </>
          )}

          <Box
            style={{
              display: noResults ? "none" : "block",
              minWidth: "100%",
              maxWidth: "0"
            }}>
            <VirtualDataTable
              ref={tableRef}
              columns={tableFields}
              rowKeyField="random_id"
              serverCallback={fetchNextPageCallback}
              height={tableHeight}
              noDataAvailable={onNoResults}
              onRowClick={onRowClick}
              onColumnChange={onColumnChange}
              hasFilterApplied={filterVisible}
            />
          </Box>
        </div>
      </Paper>

      <Outlet />
    </>
  );
};

export default Companies;
